import { BEIGE_300, BEIGE_600, BLUE_100, BLUE_600, GRAY_300, GRAY_400, GRAY_600, GRAY_700, GRAY_800, GRAY_900, WHITE } from 'constants/styling/theme'
import { Box, Stack, Typography } from '@mui/material'
import { FC, ReactNode } from 'react'

import { MUIBadge } from 'components/common/MUIBadge'
import { MUITooltip } from 'components/common/MUITooltip'
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded'
import { SliderBar } from 'components/common/SliderBar'
import { useTranslation } from 'react-i18next'

/** Building blocks of the Adjust editor's panel, in the Lab panels' vocabulary (LabPanelPrimitives) */

const BUTTON_RESET = { font: 'inherit', color: 'inherit', textAlign: 'inherit', padding: 0, border: 0, background: 'none', '&:focus-visible': { outline: `2px solid ${BLUE_600}`, outlineOffset: 2 } } as const

const SHADOW_XS = '0px 1px 4px 0px rgba(19, 24, 29, 0.06)'

export const AdjustNewBadge: FC = () => {
  const { t } = useTranslation(['photo_studio_editing'])
  return <MUIBadge label={t('adjust.new')} size="xs" color="blue" />
}

/** Group headline (Light, Color, Ratio…) with an optional action on the right (Reset all) */
export const AdjustGroupHeader: FC<{ title: string, action?: ReactNode }> = ({ title, action }) => (
  <Stack flexDirection="row" justifyContent="space-between" alignItems="center" sx={{ marginTop: '0.4rem' }}>

    <Typography sx={{ fontSize: '1.2rem', fontWeight: 550, letterSpacing: '0.04em', textTransform: 'uppercase', color: GRAY_700 }}>{title}</Typography>

    {action}

  </Stack>
)

export const AdjustTextButton: FC<{ label: string, onClick: () => void, disabled?: boolean }> = ({ label, onClick, disabled = false }) => (
  <Box
    component="button"
    type="button"
    disabled={disabled}
    onClick={onClick}
    sx={{ ...BUTTON_RESET, fontSize: '1.3rem', fontWeight: 450, color: GRAY_800, textDecoration: 'underline', textUnderlineOffset: '0.3rem', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.4 : 1 }}
  >
    {label}
  </Box>
)

interface AdjustSliderProps {
  label: string
  value: number
  valueLabel: string
  min: number
  max: number
  step?: number
  /** The neutral value; the reset button shows when the slider is off it */
  origin?: number
  onChange: (value: number) => void
  /** End of a drag or a keyboard change (closes the undo step) */
  onCommit?: () => void
  onReset?: () => void
  isNew?: boolean
  disabled?: boolean
}

/** LabSlider's look, plus a reset and a neutral mark for two-sided sliders (−100…100) */
export const AdjustSlider: FC<AdjustSliderProps> = ({ label, value, valueLabel, min, max, step = 1, origin, onChange, onCommit, onReset, isNew = false, disabled = false }) => {
  const { t } = useTranslation(['photo_studio_editing'])
  const neutral = origin ?? min
  const isTwoSided = neutral > min
  return (
    <Stack gap={0}>

      <Stack flexDirection="row" alignItems="center" gap="0.6rem">

        <Typography sx={{ fontSize: '1.4rem', fontWeight: 350, color: GRAY_700, letterSpacing: '-0.56px', lineHeight: 1.4 }}>{label}</Typography>

        {isNew && <AdjustNewBadge />}

        <Typography sx={{ marginLeft: 'auto', fontSize: '1.2rem', fontWeight: 350, color: GRAY_700, fontVariantNumeric: 'tabular-nums' }}>{valueLabel}</Typography>

        {onReset && (
          <MUITooltip content={t('adjust.reset_control', { name: label })}>
            <Box
              component="button"
              type="button"
              aria-label={t('adjust.reset_control', { name: label })}
              disabled={disabled || value === neutral}
              onClick={onReset}
              sx={{ ...BUTTON_RESET, display: 'flex', color: GRAY_600, cursor: 'pointer', opacity: value === neutral ? 0.3 : 1, '&:disabled': { cursor: 'default' }, '& .MuiSvgIcon-root': { fontSize: '1.6rem' } }}
            >
              <RestartAltRoundedIcon />
            </Box>
          </MUITooltip>
        )}

      </Stack>

      <SliderBar
        value={value}
        min={min}
        max={max}
        step={step}
        track={isTwoSided ? false : 'normal'}
        marks={isTwoSided ? [{ value: neutral }] : false}
        valueLabelDisplay="off"
        disabled={disabled}
        onChange={(_, next) => onChange(next as number)}
        onChangeCommitted={() => onCommit?.()}
        aria-label={label}
        sx={{
          width: 'calc(100% - 10px)',
          maxWidth: 'calc(100% - 10px)',
          margin: 0,
          padding: '0.8rem 0',
          height: 4,
          '& .MuiSlider-rail': { height: 4, background: GRAY_400, opacity: 1 },
          '& .MuiSlider-track': { height: 4, background: BLUE_600, border: 'none' },
          '& .MuiSlider-mark': { width: 2, height: 10, borderRadius: 1, backgroundColor: GRAY_600 },
          '& .MuiSlider-thumb': { width: 20, height: 20, background: WHITE, border: `1px solid ${GRAY_400}`, boxShadow: SHADOW_XS },
        }}
      />

    </Stack>
  )
}

interface AdjustChipProps {
  label: string
  selected?: boolean
  icon?: ReactNode
  onClick: () => void
  disabled?: boolean
  ariaLabel?: string
}

/** Pill choice (ratios, drawing tools, presets) in the Photo Studio's selected vocabulary (BLUE_600 / BLUE_100) */
export const AdjustChip: FC<AdjustChipProps> = ({ label, selected = false, icon, onClick, disabled = false, ariaLabel }) => (
  <Box
    component="button"
    type="button"
    aria-pressed={selected}
    aria-label={ariaLabel}
    disabled={disabled}
    onClick={onClick}
    sx={{
      ...BUTTON_RESET,
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.6rem',
      padding: '0.6rem 1rem',
      borderRadius: '4rem',
      border: `1px solid ${selected ? BLUE_600 : GRAY_400}`,
      backgroundColor: selected ? BLUE_100 : WHITE,
      fontSize: '1.3rem',
      fontWeight: selected ? 550 : 450,
      color: selected ? GRAY_900 : GRAY_800,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.4 : 1,
      whiteSpace: 'nowrap',
      '&:hover': { borderColor: disabled || selected ? undefined : BLUE_600 },
      '& .MuiSvgIcon-root': { fontSize: '1.7rem' },
    }}
  >

    {icon}

    {label}

  </Box>
)

export const AdjustChipGroup: FC<{ children: ReactNode, ariaLabel?: string }> = ({ children, ariaLabel }) => (
  <Stack flexDirection="row" flexWrap="wrap" gap="0.6rem" role="group" aria-label={ariaLabel}>
    {children}
  </Stack>
)

/** Square icon button with a one-line label underneath (rotate, flip, duplicate…); a long label ends in an ellipsis */
export const AdjustIconAction: FC<{ icon: ReactNode, label: string, onClick: () => void, selected?: boolean, disabled?: boolean, danger?: boolean }> = ({ icon, label, onClick, selected = false, disabled = false, danger = false }) => (
  <MUITooltip content={label}>
    <Box
      component="button"
      type="button"
      aria-label={label}
      aria-pressed={selected}
      disabled={disabled}
      onClick={onClick}
      sx={{
        ...BUTTON_RESET,
        width: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0.4rem',
        padding: '0.8rem 0.4rem',
        borderRadius: '0.8rem',
        border: `1px solid ${selected ? BLUE_600 : GRAY_300}`,
        backgroundColor: selected ? BLUE_100 : WHITE,
        color: danger ? '#C0523D' : GRAY_800,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        '&:hover': { borderColor: disabled ? undefined : BLUE_600 },
        '& .MuiSvgIcon-root': { fontSize: '2rem' },
      }}
    >

      {icon}

      <Typography sx={{ width: '100%', fontSize: '1.2rem', fontWeight: 450, lineHeight: 1.2, color: 'inherit', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</Typography>

    </Box>
  </MUITooltip>
)

/**
 * Grid of AdjustIconActions with equal columns. MUITooltip wraps each button in a <span>: the grid makes that span the
 * cell (min-width 0), so a long label ends in an ellipsis inside its card instead of pushing the row wider.
 */
export const AdjustIconActionGroup: FC<{ columns: number, children: ReactNode }> = ({ columns, children }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: '0.6rem', '& > span': { display: 'flex', minWidth: 0 } }}>
    {children}
  </Box>
)

/** Quiet explanatory note */
export const AdjustNote: FC<{ children: ReactNode }> = ({ children }) => (
  <Box sx={{ backgroundColor: BEIGE_300, border: `1px solid ${BEIGE_600}`, borderRadius: '0.8rem', padding: '0.8rem 1rem' }}>
    <Typography sx={{ fontSize: '1.3rem', fontWeight: 350, lineHeight: 1.4, color: GRAY_800 }}>{children}</Typography>
  </Box>
)

/** Label + value on one line (crop size, saved size) */
export const AdjustReadout: FC<{ label: string, value: string }> = ({ label, value }) => (
  <Stack flexDirection="row" justifyContent="space-between" alignItems="baseline">

    <Typography sx={{ fontSize: '1.3rem', fontWeight: 350, color: GRAY_700 }}>{label}</Typography>

    <Typography sx={{ fontSize: '1.3rem', fontWeight: 550, color: GRAY_900, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>

  </Stack>
)
